import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Spin
} from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs, { Dayjs } from 'dayjs'

import { useCounterpartServiceGetActiveContractListEndpointKey } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  CounterpartService,
  GetActiveContractListResponse,
  GetActiveContractListResponseItem,
  Permissions
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { Title, ClassifierInfiniteSelect } from 'src/components'
import { CounterPartSelect } from 'src/features/counterParts/components'
import { useClassifierId, useUser } from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type Props = {
  form: FormInstance
}

export const CreateInvoiceCard = ({ form }: Props) => {
  const { styles } = useStyles()
  const { t } = useLocales()
  const { userData, hasPermission } = useUser()

  const isAdmin = hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)
  const reasonsId = useClassifierId(
    isAdmin
      ? ClassifierType.RETURN_REASON
      : ClassifierType.INTERNAL_RETURN_REASON,
    ClassifierCategory.STOCKS
  )

  const selectedDate = Form.useWatch('invoiceDate', form)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      String(userData?.currentLegalEntityId),
      useCounterpartServiceGetActiveContractListEndpointKey,
      selectedDate
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await CounterpartService.getActiveContractListEndpoint(
        String(userData?.currentLegalEntityId),
        pageParam,
        DEFAULT_PER_PAGE,
        dayjs(selectedDate).format(yearMonthDaySlash)
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetActiveContractListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.flatMap(
        (item: GetActiveContractListResponseItem) =>
          item?.activeContracts?.map((contract, index) => ({
            label: `${item?.activeContracts?.[index]?.counterpart?.name} (${t('organizational_structure.idno')}: ${item?.activeContracts?.[index]?.counterpart?.idno}, ${t('counterparts.contract_number')}: ${contract?.contractNumber})`,
            value: contract.id,
            item
          })) ?? []
      ) ?? []
  )

  const showTransportInfo = Form.useWatch('showTransportInfo', form)

  return (
    <Card>
      <Flex vertical gap={24}>
        <Title className={styles.title}>
          {t('stock_returns.register_return_invoice')}
        </Title>

        <Form.Item shouldUpdate>
          {({ getFieldValue }) => {
            const isContractDisabled = !getFieldValue('invoiceDate')

            const isDisabled =
              !getFieldValue('invoiceDate') || !getFieldValue('contractId')

            const disabledStartDate = (current: Dayjs) => {
              return (
                current &&
                current <
                  dayjs(getFieldValue('invoiceDate'))
                    .subtract(1, 'day')
                    .endOf('day')
              )
            }

            return (
              <Row gutter={[24, 16]} className={styles.formWrapper}>
                <Col span={12}>
                  <Form.Item
                    name="invoiceDate"
                    label={t(`stock.invoice_date`)}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      format={dayMonthYearBackSlash}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="invoiceNumber"
                    label={t(`stock.accompanying_invoice`)}
                    rules={requiredRules}
                  >
                    <Input
                      className="full-width"
                      disabled={isDisabled}
                      placeholder={t(`stock.accompanying_invoice`)}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="contractId"
                    label={t(`counterparts.purchase_center`)}
                    rules={requiredRules}
                  >
                    <Select
                      popupMatchSelectWidth={true}
                      filterOption={false}
                      disabled={isContractDisabled}
                      options={options}
                      loading={isFetching}
                      placeholder={t('counterparts.purchase_center')}
                      showSearch={true}
                      onSelect={(value) => {
                        const selectedContract = options?.find(
                          (option) => option.value === value
                        )

                        form.setFieldsValue({
                          purchase_center:
                            selectedContract?.item?.beneficiary?.name
                        })
                      }}
                      onPopupScroll={(e) => {
                        const target = e.target as HTMLElement
                        if (
                          target.scrollTop + target.offsetHeight ===
                          target.scrollHeight
                        )
                          fetchNextPage()
                      }}
                      notFoundContent={
                        isFetching ? (
                          <Flex justify="center">
                            <Spin />
                          </Flex>
                        ) : (
                          <Empty />
                        )
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="purchase_center"
                    label={t(`stock.provider`)}
                    rules={requiredRules}
                  >
                    <Input
                      className="full-width"
                      disabled
                      placeholder={t(`stock.provider`)}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="takeoverDate"
                    label={t(`stock_administration.pickup_date`)}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      disabled={isDisabled}
                      disabledDate={disabledStartDate}
                      format={dayMonthYearBackSlash}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="reasons"
                    label={t(`stock.return_reason`)}
                    rules={requiredRules}
                  >
                    <ClassifierInfiniteSelect
                      disabled={isDisabled}
                      id={reasonsId}
                      mode="multiple"
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name="showTransportInfo" valuePropName="checked">
                    <Checkbox>
                      {t('stock_administration.register_transport')}{' '}
                    </Checkbox>
                  </Form.Item>
                </Col>

                {showTransportInfo && (
                  <Col span={24}>
                    <Card>
                      <Row gutter={24}>
                        <Col span={12}>
                          <Form.Item
                            name="transporterId"
                            label={t(`stock_administration.select_transport`)}
                            rules={requiredRules}
                          >
                            <CounterPartSelect
                              allowClear={false}
                              placeholder={t(
                                `stock_administration.select_transport`
                              )}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={12}>
                          <Form.Item
                            name="transporterAccompanyingNotice"
                            label={t(`stock_administration.way_bill`)}
                            rules={requiredRules}
                          >
                            <Input
                              className="full-width"
                              placeholder={t(`stock_administration.way_bill`)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                )}
              </Row>
            )
          }}
        </Form.Item>
      </Flex>
    </Card>
  )
}
